import { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import axiosInstance from '../../helpers/axios';
import { useInstituteOptions } from '../FibeCommissionBilling/hooks/useInstituteOptions';
import { ROLE_LABEL_BY_VALUE, ROLE_SCOPE_MAP, ALL_SCOPE_TYPES } from './roleOptions';

const ROLE_GROUPS = [
    { label: 'L1 Executive', roles: ['collection_user', 'end-user'] },
    { label: 'L2 Zonal', roles: ['zonal_processing', 'zonal_collection'] },
    { label: 'L3 Manager', roles: ['collection_manager', 'crm'] },
    { label: 'L4 Analyst', roles: ['cra', 'crsa', 'institute_cra'] },
    { label: 'L5 Leadership', roles: ['ceo', 'cxo', 'admin'] },
    { label: 'L6 Finance', roles: ['finance', 'accounts'] },
    { label: 'L7 External', roles: ['fund_manager', 'fund_user', 'institute_admin', 'institute_user', 'co_lender', 'channel_user'] },
];

// Keeps long option lists from overflowing the viewport and getting shifted above the select.
const SELECT_MENU_PROPS = {
    variant: 'menu',
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: { style: { maxHeight: 250 } },
};

const AUTOCOMPLETE_POPPER_PROPS = {
    placement: 'bottom-start',
    modifiers: [{ name: 'flip', enabled: false }],
};

export default function AssignRoleModal({ open, onClose, user, onSuccess }) {
    const [selectedUser, setSelectedUser] = useState(user || null);
    const [userSearch, setUserSearch] = useState('');
    const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
    const [userOptions, setUserOptions] = useState([]);

    const [role, setRole] = useState('');
    const [scopeType, setScopeType] = useState('');
    const [zoneId, setZoneId] = useState('');
    const [teamId, setTeamId] = useState('');
    const [assignedInstitutes, setAssignedInstitutes] = useState([]);
    const [fundId, setFundId] = useState('');

    const [zones, setZones] = useState([]);
    const [teams, setTeams] = useState([]);
    const [funds, setFunds] = useState([]);
    const { institutes } = useInstituteOptions();

    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Reset the form whenever the modal is (re)opened, for whichever user it was opened for.
    useEffect(() => {
        if (!open) return;
        setSelectedUser(user || null);
        setRole(user?.role || '');
        setScopeType(user?.scopeType || '');
        setZoneId(user?.zoneId || '');
        setTeamId(user?.teamId || '');
        setAssignedInstitutes(user?.assignedInstitutes || []);
        setFundId(user?.fundId || '');
        setErrorMessage(null);
        setUserSearch('');
        setUserOptions([]);
    }, [open, user]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedUserSearch(userSearch), 300);
        return () => clearTimeout(handle);
    }, [userSearch]);

    useEffect(() => {
        if (user || !open || !debouncedUserSearch) return;
        axiosInstance
            .get('/admin-settings/users', { params: { page: 1, limit: 20, search: debouncedUserSearch } })
            .then((response) => setUserOptions(response.data.data.users))
            .catch(() => setUserOptions([]));
    }, [debouncedUserSearch, user, open]);

    useEffect(() => {
        if (!open) return;
        axiosInstance.get('/admin-settings/zones').then((response) => setZones(response.data.data)).catch(() => setZones([]));
        axiosInstance.get('/admin-settings/teams').then((response) => setTeams(response.data.data)).catch(() => setTeams([]));
        axiosInstance.get('/admin-settings/funds').then((response) => setFunds(response.data.data.funds)).catch(() => setFunds([]));
    }, [open]);

    const validScopes = role ? (ROLE_SCOPE_MAP[role] || ALL_SCOPE_TYPES) : [];

    // Auto-select the only valid scope for the chosen role; clear an unsupported scope.
    useEffect(() => {
        if (!role) return;
        if (validScopes.length === 1) {
            setScopeType(validScopes[0]);
        } else if (scopeType && !validScopes.includes(scopeType)) {
            setScopeType('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    const handleSave = () => {
        if (!selectedUser) return;
        setIsSaving(true);
        setErrorMessage(null);
        axiosInstance
            .put(`/admin-settings/users/${selectedUser.id}/role`, {
                role,
                scopeType,
                zoneId: scopeType === 'zone' ? zoneId : null,
                teamId: scopeType === 'team' ? teamId : null,
                assignedInstitutes: scopeType === 'team' ? assignedInstitutes : null,
                fundId: scopeType === 'fund' ? fundId : null,
            })
            .then(() => {
                onSuccess?.();
                onClose();
            })
            .catch((error) => {
                setErrorMessage(error?.response?.data?.message || 'Could not save role.');
            })
            .finally(() => setIsSaving(false));
    };

    const canSave =
        Boolean(selectedUser) &&
        Boolean(role) &&
        Boolean(scopeType) &&
        (scopeType !== 'zone' || Boolean(zoneId)) &&
        (scopeType !== 'team' || assignedInstitutes.length > 0) &&
        (scopeType !== 'fund' || Boolean(fundId));

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Assign Role</DialogTitle>
            <DialogContent>
                {errorMessage && (
                    <Alert severity="error" sx={{ marginBottom: '16px' }}>{errorMessage}</Alert>
                )}

                {user ? (
                    <Box sx={{ marginBottom: '16px' }}>
                        <Box sx={{ fontWeight: 500 }}>{user.name}</Box>
                        <Box sx={{ fontSize: 13, color: 'text.secondary' }}>{user.email}</Box>
                    </Box>
                ) : (
                    <Autocomplete
                        options={userOptions}
                        getOptionLabel={(option) => `${option.name} (${option.email})`}
                        value={selectedUser}
                        onChange={(e, value) => setSelectedUser(value)}
                        onInputChange={(e, value) => setUserSearch(value)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Search by name or email..." />
                        )}
                        ListboxProps={{ style: { maxHeight: 250 } }}
                        slotProps={{ popper: AUTOCOMPLETE_POPPER_PROPS }}
                        sx={{ marginBottom: '16px' }}
                    />
                )}

                <FormControl fullWidth sx={{ marginBottom: '16px' }}>
                    <InputLabel shrink>Role</InputLabel>
                    <Select
                        displayEmpty
                        label="Role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        MenuProps={SELECT_MENU_PROPS}
                    >
                        <MenuItem value="" disabled>Select role</MenuItem>
                        {ROLE_GROUPS.flatMap((group) => [
                            <ListSubheader key={group.label}>{group.label}</ListSubheader>,
                            ...group.roles.map((r) => (
                                <MenuItem key={r} value={r}>{ROLE_LABEL_BY_VALUE[r] || r}</MenuItem>
                            )),
                        ])}
                    </Select>
                </FormControl>

                <FormControl fullWidth sx={{ marginBottom: '16px' }} disabled={!role || validScopes.length === 1}>
                    <InputLabel shrink>Scope Type</InputLabel>
                    <Select
                        displayEmpty
                        label="Scope Type"
                        value={scopeType}
                        onChange={(e) => setScopeType(e.target.value)}
                        MenuProps={SELECT_MENU_PROPS}
                    >
                        <MenuItem value="" disabled>Select scope type</MenuItem>
                        {validScopes.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {scopeType === 'zone' && (
                    <FormControl fullWidth sx={{ marginBottom: '16px' }}>
                        <InputLabel shrink>Zone</InputLabel>
                        <Select
                            displayEmpty
                            label="Zone"
                            value={zoneId}
                            onChange={(e) => setZoneId(e.target.value)}
                            MenuProps={SELECT_MENU_PROPS}
                        >
                            <MenuItem value="" disabled>Select zone</MenuItem>
                            {zones.map((z) => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}

                {scopeType === 'team' && (
                    <>
                        <FormControl fullWidth sx={{ marginBottom: '16px' }}>
                            <InputLabel shrink>Team</InputLabel>
                            <Select
                                displayEmpty
                                label="Team"
                                value={teamId}
                                onChange={(e) => {
                                    const newTeamId = e.target.value;
                                    setTeamId(newTeamId);
                                    const team = teams.find((t) => t.id === newTeamId);
                                    setAssignedInstitutes(team?.assignedInstitutes || []);
                                }}
                                MenuProps={SELECT_MENU_PROPS}
                            >
                                <MenuItem value="" disabled>Select team</MenuItem>
                                {teams.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Autocomplete
                            multiple
                            options={institutes.filter((i) => (teams.find((t) => t.id === teamId)?.assignedInstitutes || []).includes(i.id))}
                            getOptionLabel={(option) => option.brandName || option.id}
                            value={institutes.filter((i) => assignedInstitutes.includes(i.id))}
                            onChange={(e, value) => setAssignedInstitutes(value.map((v) => v.id))}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option.brandName} size="small" {...getTagProps({ index })} />
                                ))
                            }
                            renderInput={(params) => <TextField {...params} label="Assigned Institutes" />}
                            ListboxProps={{ style: { maxHeight: 250 } }}
                            slotProps={{ popper: AUTOCOMPLETE_POPPER_PROPS }}
                            sx={{ marginBottom: '16px' }}
                        />
                    </>
                )}

                {scopeType === 'fund' && (
                    <FormControl fullWidth sx={{ marginBottom: '16px' }}>
                        <InputLabel shrink>Fund</InputLabel>
                        <Select
                            displayEmpty
                            label="Fund"
                            value={fundId}
                            onChange={(e) => setFundId(e.target.value)}
                            MenuProps={SELECT_MENU_PROPS}
                        >
                            <MenuItem value="" disabled>Select fund</MenuItem>
                            {funds.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave || isSaving} sx={{ color: '#fff' }}>
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
